import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { MdDoubleArrow } from 'react-icons/md';
import { IoMdMenu } from 'react-icons/io';
import { FaChevronDown } from 'react-icons/fa6';
import { useUser } from '../../context/UserContext';
import { BOOKING_PAGE_URL, HOME_PAGE_URL } from '../../utils/Routes';
import { CompanyName } from '../../utils/Constants';
import useNavigateToPage from '../../hooks/useNavigateToPage';
import { getNavLinkItemsArray } from '../../utils/data/NavData';
import { removeToken } from '../../utils/user/token';
import SocialMediaCTA from '../socialMedia/SocialMediaCTA';

function Navbar() {
  const { user, setUser } = useUser();
  const navigateToPage = useNavigateToPage();
  const navLinkItemsArray = getNavLinkItemsArray(user);

  const [isPhoneNavOpen, setIsPhoneNavOpen] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const [showNavbar, setShowNavbar] = useState(true);
  const [scrolled, setScrolled] = useState(false); // for bg/shadow

  // scroll bookkeeping
  const lastScrollYRef = useRef(0);
  const tickingRef = useRef(false);

  const THRESHOLD = 12; // px of movement before we react

  const togglePhoneNav = () => setIsPhoneNavOpen((prev) => !prev);

  const handleLogout = (event) => {
    event.preventDefault();
    setUser({});
    removeToken();
    navigateToPage(HOME_PAGE_URL, { replace: true });
  };

  const handleMobileDropdownToggle = (label) => {
    setOpenMobileDropdown((prev) => (prev === label ? null : label));
  };

  useEffect(() => {
    // initialize lastScrollY on mount (prevents instant hide on first tiny scroll)
    lastScrollYRef.current = typeof window !== 'undefined' ? window.scrollY : 0;

    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;

      requestAnimationFrame(() => {
        const y = window.scrollY || 0;

        // compute scrolled state for bg/shadow
        setScrolled(y > 8);

        // do not auto-hide when phone nav is open
        if (!isPhoneNavOpen) {
          const delta = y - lastScrollYRef.current;

          // at very top, always show
          if (y <= 0) {
            setShowNavbar(true);
          } else if (Math.abs(delta) > THRESHOLD) {
            if (delta > 0) {
              // scrolling down
              setShowNavbar(false);
            } else {
              // scrolling up
              setShowNavbar(true);
            }
          }
        }

        lastScrollYRef.current = y;
        tickingRef.current = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isPhoneNavOpen]);

  // optional: lock body scroll when phone nav is open
  useEffect(() => {
    document.body.style.overflow = isPhoneNavOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isPhoneNavOpen]);

  return (
    <nav
      role='navigation'
      aria-label='Main Navigation'
      className={[
        'fixed w-full top-0 left-0 z-50 transition-transform duration-300 will-change-transform',
        showNavbar ? 'translate-y-0' : '-translate-y-full',
        // bg/shadow only when scrolled; keep your color tokens
        scrolled
          ? 'bg-colour5/10 shadow-md shadow-black/10 backdrop-blur'
          : 'bg-transparent',
      ].join(' ')}
    >
      {/* ========== TOP ROW ========== */}
      <section className='grid grid-cols-reg px-6 py-6'>
        {/* Brand */}
        <section className='grid items-center'>
          <NavLink to={HOME_PAGE_URL} className='grid items-center'>
            <span className='text-colour5 font-titleFont hover:brightness-90'>
              {CompanyName}
            </span>
          </NavLink>
        </section>

        {/* Right side: desktop nav + mobile button */}
        <section className='grid justify-end'>
          {/* Mobile menu button */}
          <button
            aria-label='Toggle navigation menu'
            onClick={togglePhoneNav}
            className='grid md:hidden w-fit h-fit items-center justify-center text-4xl text-colour5 cursor-pointer'
          >
            <IoMdMenu className='active:scale-90 duration-300' />
          </button>

          {/* Desktop navigation */}
          <ul className='hidden lg:grid grid-flow-col gap-6 items-center text-colour5'>
            {navLinkItemsArray.map(({ path, label, subpages }) =>
              subpages ? (
                <li key={label} className='relative group'>
                  <span
                    className='text-xl md:text-sm text-colour8 dark:text-colour1 font-semibold font-poppins hover:brightness-90 duration-200 active:scale-75 cursor-pointer grid grid-flow-col items-center gap-2'
                    aria-haspopup='true'
                    aria-expanded='false'
                  >
                    {label}
                    <span className='grid items-center'>
                      <FaChevronDown className='text-colour8 dark:text-colour1' />
                    </span>
                  </span>

                  {/* Dropdown menu (desktop hover) */}
                  <ul
                    className='absolute left-0 top-full grid gap-2 bg-colour1 dark:bg-colour2 shadow-cardShadowBold rounded-md p-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-200 min-w-32'
                    role='menu'
                    aria-label={`${label} submenu`}
                  >
                    {subpages.map(({ path: sp, label: sl }) => (
                      <div
                        key={sl}
                        className='grid border-b border-solid border-colour5 last:border-none'
                      >
                        <NavItem url={sp} title={sl} />
                      </div>
                    ))}
                  </ul>
                </li>
              ) : (
                <NavItem key={label} url={path} title={label} />
              )
            )}

            <ul>
              <NavItem2 url={BOOKING_PAGE_URL} title='BOOK NOW' />
            </ul>

            {user?.email && (
              <li>
                <button
                  className='text-xl md:text-sm text-colour8 dark:text-colour1 font-semibold font-poppins hover:brightness-90 duration-200 active:scale-75'
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </li>
            )}
          </ul>
        </section>
      </section>

      {/* ========== MOBILE NAV ========== */}
      <section
        className={[
          'phone-nav absolute top-full left-0 w-full bg-colour1 dark:bg-colour2 border-b-4 border-solid border-colour5 transition-transform duration-300',
          isPhoneNavOpen
            ? 'translate-y-0 opacity-100'
            : '-translate-y-full opacity-0',
        ].join(' ')}
      >
        <div className='grid py-12 gap-y-10'>
          <ul className='grid gap-10 items-center justify-center text-center text-colour5'>
            {navLinkItemsArray.map(({ path, label, subpages }) =>
              subpages ? (
                <li key={label} className='grid gap-2'>
                  <button
                    onClick={() => handleMobileDropdownToggle(label)}
                    className='grid grid-flow-col gap-3 justify-center items-center text-xl md:text-lg text-colour8 dark:text-colour1 font-semibold font-poppins hover:brightness-90 duration-200 active:scale-75'
                    aria-haspopup='true'
                    aria-expanded={openMobileDropdown === label}
                    aria-controls={`mobile-submenu-${label}`}
                  >
                    <span>{label}</span>
                    <span
                      className={[
                        'grid transition-transform duration-300',
                        openMobileDropdown === label ? 'rotate-180' : '',
                      ].join(' ')}
                    >
                      <FaChevronDown />
                    </span>
                  </button>

                  {openMobileDropdown === label && (
                    <ul
                      id={`mobile-submenu-${label}`}
                      className='grid gap-y-8 py-6'
                      role='menu'
                      aria-label={`${label} submenu`}
                    >
                      {subpages.map(({ path: sp, label: sl }) => (
                        <NavItem
                          key={sl}
                          url={sp}
                          title={sl}
                          onClick={() => setIsPhoneNavOpen(false)}
                        />
                      ))}
                    </ul>
                  )}
                </li>
              ) : (
                <NavItem
                  key={label}
                  url={path}
                  title={label}
                  onClick={() => setIsPhoneNavOpen(false)}
                />
              )
            )}

            {user?.email && (
              <li>
                <button
                  className='text-xl md:text-sm text-colour8 dark:text-colour1 font-semibold font-poppins hover:brightness-90 duration-200 active:scale-75'
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </li>
            )}
          </ul>

          <div className='text-colour5'>
            <SocialMediaCTA />
          </div>
        </div>
      </section>
    </nav>
  );
}

const NavItem = ({ url, title, onClick }) => (
  <li className='active:scale-90'>
    <NavLink
      to={url}
      onClick={onClick}
      aria-label={`${title} page navigation tab`}
      className='text-xl md:text-sm text-colour8 dark:text-colour3 font-semibold font-poppins hover:brightness-90 duration-200 active:scale-75'
      aria-current={({ isActive }) => (isActive ? 'page' : undefined)}
      style={({ isActive }) => (isActive ? { color: '#3b82f6' } : {})}
    >
      {title}
    </NavLink>
  </li>
);

const NavItem2 = ({ url, title }) => (
  <li>
    <NavLink
      to={url}
      aria-label={`${title} page navigation tab`}
      className='grid grid-cols-rev items-center gap-x-8 text-xl md:text-sm px-2 py-2 bg-colour5 text-colour1 font-semibold font-poppins hover:brightness-90 duration-200 active:scale-75'
      aria-current={({ isActive }) => (isActive ? 'page' : undefined)}
      style={({ isActive }) => (isActive ? { color: '#f8fafc' } : {})}
    >
      <span>{title}</span>
      <MdDoubleArrow className='text-xl' />
    </NavLink>
  </li>
);

export default Navbar;
